import { useEffect } from 'react'

import { useBulkSelector } from '~/shared/hooks/useBulkSelector'
import { eventLogger } from '~/shared/services/eventLogger'
import {
  selectCohortID,
  selectGrowthBookExperiments,
  selectLanguage,
} from '~/shared/store/common/selects'
import {
  selectUserCountryCode,
  selectUserUUID,
} from '~/shared/store/user/selects'

import { growthBookInstance } from '../lib/growthBook'

const useSelectors = () =>
  useBulkSelector({
    uuid: selectUserUUID,
    countryCode: selectUserCountryCode,
    cohortId: selectCohortID,
    language: selectLanguage,
    growthBookExperiments: selectGrowthBookExperiments,
  })

export const useGrowthBookInitialization = (): void => {
  const { uuid, countryCode, cohortId, language, growthBookExperiments } =
    useSelectors()

  // Set synchronously during render (not in a useEffect) so that any child
  // component reading a feature/experiment value via `useFeatureValue` in the
  // same render pass always sees up-to-date attributes. If this ran in an
  // effect instead, there would be a one-commit window on first mount where a
  // descendant evaluating a feature could read stale/partial attributes (e.g.
  // an empty `country` before the user status request resolves), causing
  // GrowthBook to wrongly match a targeting condition like `country is none
  // of (US)` and fire a bogus experiment exposure event.
  growthBookInstance.setAttributes({
    id: uuid,
    country: countryCode,
    cohort: cohortId,
    language,
  })

  useEffect(() => {
    growthBookInstance.setURL(window.location.href)
  }, [uuid, countryCode, cohortId, language])

  useEffect(() => {
    const unsubscribe = growthBookInstance.subscribe((experiment, result) => {
      if (!growthBookExperiments || !result.hashUsed) return

      const experimentKey = experiment.key || ''
      const variantId = String(result.variationId)

      eventLogger.logGrowthBookAbSegmentName({
        variantId,
        experimentKey,
        variantName: growthBookExperiments?.[experimentKey]?.[variantId],
      })
    })

    return () => unsubscribe()
  }, [growthBookExperiments])
}
